import { AxiosInstance, AxiosResponse } from "axios";
import { AdminCarrierBridgeResponse } from "../CarrierBridgeData";

/**
 * The admin list endpoints page at 100 by default; the table and the port
 * picker work on the client, so ask for the backend's maximum in one call.
 */
const ADMIN_LIST_LIMIT = 1000;

const CARRIER_BRIDGE_PATH = "/resource-manager/ke-carrier-bridge";

interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

export type CarrierBridgeListResponse = ApiEnvelope<AdminCarrierBridgeResponse[]>;

/** GET /v1/resource-manager/ke-carrier-bridge — every tenant, drafts included. */
export function fetchCarrierBridgeList(
  api: AxiosInstance
): Promise<AxiosResponse<CarrierBridgeListResponse>> {
  return api.get<CarrierBridgeListResponse>(CARRIER_BRIDGE_PATH, {
    params: { limit: ADMIN_LIST_LIMIT },
  });
}

/**
 * Body of create (POST) and update (PUT). On update every field is optional
 * and only the ones sent change; `subscriptionId` and `tenantId` are never
 * editable (the backend refuses them).
 */
export interface CarrierBridgePayload {
  kontrolEdgePortId?: number;
  carrierId?: number;
  mbps?: number;
  /** Null takes the whole port. */
  vlan?: string | null;
  circuitId?: string | null;
  name?: string;
  isDraft?: boolean;
}

export function createCarrierBridge(
  api: AxiosInstance,
  payload: CarrierBridgePayload
): Promise<AxiosResponse<ApiEnvelope<AdminCarrierBridgeResponse>>> {
  return api.post(CARRIER_BRIDGE_PATH, payload);
}

export function updateCarrierBridge(
  api: AxiosInstance,
  id: number,
  payload: CarrierBridgePayload
): Promise<AxiosResponse<ApiEnvelope<AdminCarrierBridgeResponse>>> {
  return api.put(`${CARRIER_BRIDGE_PATH}/${id}`, payload);
}

export function deleteCarrierBridge(
  api: AxiosInstance,
  id: number
): Promise<AxiosResponse<ApiEnvelope<unknown>>> {
  return api.delete(`${CARRIER_BRIDGE_PATH}/${id}`);
}

/** A carrier from the catalog (chopper-backend CTX-710 / CTX-785). */
export interface CarrierOption {
  id: number;
  name: string;
  code: string | null;
  available: boolean;
}

/** GET /v1/resource-manager/carriers — only the ones open to new services. */
export function fetchAvailableCarriers(
  api: AxiosInstance
): Promise<AxiosResponse<ApiEnvelope<CarrierOption[]>>> {
  return api.get("/resource-manager/carriers");
}

/** The fields of an admin port record the Carrier Bridge form needs. */
export interface PortOption {
  id: number;
  subid: string | null;
  name: string | null;
  tenantId: number | null;
  tenant?: { id: number; Name: string | null; nameDisplay: string | null } | null;
  datacenter?: { datacenterId: string; name: string | null; displayName: string | null } | null;
  portAllocation?: { id: number; portNumber: string | null } | null;
}

/** GET /v1/resource-manager/ke-ports — provisioned ports of every tenant. */
export function fetchPortOptions(
  api: AxiosInstance
): Promise<AxiosResponse<ApiEnvelope<PortOption[]>>> {
  return api.get("/resource-manager/ke-ports", {
    params: { isDraft: false, limit: ADMIN_LIST_LIMIT },
  });
}
